import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useCallback, useState } from "react";
import { AppHeaderBar } from "@/components/ui/app-header";
import type { ReactElement } from "react";
import Link from "next/link";
import { BarChart3, Bell, Github, SettingsIcon } from "lucide-react";
import Sidebar from "./sidebar/Sidebar";
import { OrgUrlSyncProvider } from "./OrgUrlSyncProvider";
import AppSwitcherRail from "./sidebar/AppSwitcherRail";
import { useActiveApp, isCodesmithPath, isSettingsPath, isStandaloneAccountPath } from "./sidebar/useActiveApp";
import SettingsSidebar from "./sidebar/SettingsSidebar";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import SidebarSkeleton from "./sidebar/SidebarSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { SessionSkeleton } from "./codesmith/SessionSkeleton";
import { BlacksmithLogomarkAnimationSVG, BlacksmithLogomarkSVG } from "./BlacksmithLogoSVG";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { RAIL_ROW, RAIL_ROW_ACTIVE } from "./sidebar/SidebarGlobalChrome";
import { RailStatusWidgetSkeleton } from "./sidebar/StatusIndicator";
import { useLocalStorage } from "usehooks-ts";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { ShortcutsModal } from "./ShortcutsModal";
import LiveMetricsSheetLoader from "./sidebar/LiveMetricsSheetLoader";
import { useShortcuts } from "@/hooks/useShortcuts";
import { CommandPalette } from "./CommandPalette";
import { useWorkflowCache } from "@/hooks/useWorkflowCache";
import { usePrefetchAssistantSessions } from "@/hooks/usePrefetchAssistantSessions";
import { usePrefetchCodesmithChunk } from "@/hooks/usePrefetchCodesmithChunk";
import { RAIL_WIDTH } from "./sidebar/railConstants";
import { getCachedCodesmithEnabled, getCachedLastActiveOrg } from "./sidebar/useCodesmithEnabledCache";

// Helper function to read sidebar state from localStorage synchronously
// This ensures the skeleton matches the actual sidebar state even during initial render
function getSidebarStateFromStorage(): boolean {
  if (typeof window === "undefined") {
    return true; // Default to expanded on SSR
  }
  try {
    const stored = localStorage.getItem("sidebar-open");
    if (stored === null) {
      return true; // Default to expanded if not set
    }
    return stored === "true";
  } catch {
    return true; // Default to expanded on error
  }
}

/**
 * Twin of the mounted rail's nav row: same 32px footprint, icon box, and
 * resting icon color. With an href it is a live Link with the rail's hover
 * and focus affordances, so a click landing during the skeleton window
 * navigates instead of dying on placeholder chrome; without one it stays a
 * plain div that shouldn't advertise interactivity.
 */
function RailSkeletonRow(t0) {
  const $ = _c(15);
  const {
    icon,
    active,
    slot,
    href
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const contents = t1;
  if (href) {
    const t2 = active && RAIL_ROW_ACTIVE;
    let t3;
    if ($[2] !== t2) {
      t3 = cn(RAIL_ROW, t2);
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== contents || $[5] !== href || $[6] !== slot || $[7] !== t3) {
      t4 = <Link href={href} data-slot={slot} className={t3}>{contents}</Link>;
      $[4] = contents;
      $[5] = href;
      $[6] = slot;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  const t2 = active && RAIL_ROW_ACTIVE;
  let t3;
  if ($[9] !== t2) {
    t3 = cn("flex h-8 w-full items-center overflow-hidden rounded-md", "[&_svg]:text-neutral-600 dark:[&_svg]:text-neutral-400", t2);
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== contents || $[12] !== slot || $[13] !== t3) {
    t4 = <div data-slot={slot} className={t3}>{contents}</div>;
    $[11] = contents;
    $[12] = slot;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}

/**
 * Placeholder matching AppSwitcherRail's slim footprint: logo, org-switcher
 * pill, app rows, and the global footer (three icon rows + status widget), so
 * the rail never sits as a blank strip while user/org data loads. Every fixed
 * glyph is the mounted rail's own icon rather than a gray block: none of them
 * depend on the data being awaited, so a placeholder there would only flash
 * before the identical icon lands. Only the org pill, whose content is the
 * data being loaded, stays a `Skeleton`; the status widget is a static twin at
 * its own pending values (see RailStatusWidgetSkeleton), which keeps the footer
 * rows above it at their final height. The Codesmith app row follows the same
 * gate the mounted rail uses, so nothing pops in or out on swap.
 */
function RailSkeleton() {
  const $ = _c(30);
  const router = useRouter();
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  const isCodesmithRoute = isCodesmithPath(router.pathname);
  let t0;
  if ($[0] !== urlOrg) {
    t0 = getCachedCodesmithEnabled(urlOrg);
    $[0] = urlOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const showCodesmithRow = t0 !== false;
  let t1;
  if ($[2] !== urlOrg) {
    t1 = urlOrg || getCachedLastActiveOrg();
    $[2] = urlOrg;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const navOrg = t1;
  const activeSection = router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isStandaloneAccountPath(router.pathname) ? null : isCodesmithRoute ? "codesmith" : "actions";
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: RAIL_WIDTH
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AppHeaderBar><div className="flex size-8 shrink-0 items-center justify-center"><div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div></div></AppHeaderBar>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="pt-app-header-gap"><Skeleton className="h-8 w-full rounded-lg" /></div>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Github className="size-[18px]" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = activeSection === "actions";
  const t7 = navOrg ? `/${navOrg}/runs/jobs` : undefined;
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <RailSkeletonRow icon={t5} active={t6} href={t7} />;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== activeSection || $[12] !== navOrg || $[13] !== showCodesmithRow) {
    t9 = showCodesmithRow && <RailSkeletonRow icon={<CodesmithIcon />} active={activeSection === "codesmith"} slot="rail-skeleton-codesmith-row" href={navOrg ? `/${navOrg}/sessions/new` : undefined} />;
    $[11] = activeSection;
    $[12] = navOrg;
    $[13] = showCodesmithRow;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div className="flex flex-col gap-1 pt-1">{t8}{t9}</div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex-1" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <BarChart3 className="size-[18px]" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = activeSection === "usage";
  let t14;
  if ($[20] !== t13) {
    t14 = <RailSkeletonRow icon={t12} active={t13} />;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <RailSkeletonRow icon={<SettingsIcon className="size-[18px]" />} />;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <RailSkeletonRow icon={<Bell className="size-[18px]" />} />;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="pt-1"><RailStatusWidgetSkeleton /></div>;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t14) {
    t18 = <div className="flex flex-col gap-1 pb-2">{t14}{t15}{t16}{t17}</div>;
    $[25] = t14;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t10 || $[28] !== t18) {
    t19 = <div className="h-full shrink-0 bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]" style={t2} data-slot="app-switcher-rail-skeleton"><div className="flex h-full w-full flex-col px-2">{t3}{t4}{t10}{t11}{t18}</div></div>;
    $[27] = t10;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  return t19;
}

/**
 * Full-page loading skeleton: rail + sidebar skeleton + centered logo in main
 * area, or rail + SessionSkeleton on codesmith routes (which have no Actions
 * sidebar; the session sidebar lives inside the main pane at a persisted width).
 * Used during initial auth check (AuthProvider) so the loading state matches app chrome.
 * AuthProvider only renders this on the client after hydration, so
 * localStorage can be read synchronously on first paint.
 */
export function SidebarLayoutSkeleton() {
  const $ = _c(17);
  const router = useRouter();
  const [sidebarOpen] = useState(getSidebarStateFromStorage);
  let t0;
  if ($[0] !== router.pathname) {
    t0 = isCodesmithPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isCodesmithRoute = t0;
  let t1;
  if ($[2] !== router.pathname) {
    t1 = isStandaloneAccountPath(router.pathname);
    $[2] = router.pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isStandalone = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RailSkeleton />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isCodesmithRoute || $[6] !== isStandalone || $[7] !== sidebarOpen) {
    t3 = !isCodesmithRoute && <SidebarSkeleton collapsed={!sidebarOpen} standalone={isStandalone} />;
    $[5] = isCodesmithRoute;
    $[6] = isStandalone;
    $[7] = sidebarOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== isCodesmithRoute) {
    t4 = <SidebarInset className="flex-1 bg-transparent! overflow-y-auto [scrollbar-gutter:stable]">{isCodesmithRoute ? <SessionSkeleton /> : <div className="flex h-full items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>}</SidebarInset>;
    $[9] = isCodesmithRoute;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex-1 flex overflow-hidden">{t2}{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== sidebarOpen || $[15] !== t5) {
    t6 = <div className="flex flex-col h-dvh"><SidebarProvider open={sidebarOpen} className="flex-1 min-h-0">{t5}</SidebarProvider></div>;
    $[14] = sidebarOpen;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
export function SidebarLayout(t0) {
  const $ = _c(48);
  const {
    children,
    scrollableMain: t1
  } = t0;
  const scrollableMain = t1 === undefined ? true : t1;
  const [sidebarOpen, setSidebarOpen] = useLocalStorage("sidebar-open", true);
  const [sidebarPreference, setSidebarPreference] = useLocalStorage("sidebar-preference", "auto");
  const {
    shortcutsModalOpen,
    setShortcutsModalOpen
  } = useShortcuts();
  const router = useRouter();
  const fixedMain = scrollableMain === false || /^(\/\[org\])?\/(usage|settings|sessions)(\/|$)/.test(router.pathname) || router.pathname === "/admin" || router.pathname === "/[org]/cache/[repository]";
  let t2;
  if ($[0] !== router.asPath || $[1] !== router.query.embed) {
    t2 = router.query.embed === "1" || /(?:^|[?&])embed=1(?:&|$)/.test(router.asPath.split("#")[0] ?? "");
    $[0] = router.asPath;
    $[1] = router.query.embed;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isEmbed = t2;
  const activeApp = useActiveApp();
  let t3;
  if ($[3] !== router.pathname) {
    t3 = isStandaloneAccountPath(router.pathname);
    $[3] = router.pathname;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isStandalone = t3;
  const showContextualSidebar = !isEmbed && activeApp === "actions" && !isStandalone;
  let t4;
  if ($[5] !== isEmbed || $[6] !== router.pathname) {
    t4 = !isEmbed && isSettingsPath(router.pathname);
    $[5] = isEmbed;
    $[6] = router.pathname;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const showSettingsSidebar = t4;
  useWorkflowCache();
  usePrefetchAssistantSessions();
  usePrefetchCodesmithChunk();
  let t5;
  if ($[8] !== setSidebarOpen || $[9] !== setSidebarPreference) {
    t5 = open => {
      setSidebarOpen(open);
      setSidebarPreference("manual");
    };
    $[8] = setSidebarOpen;
    $[9] = setSidebarPreference;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleSidebarChange = t5;
  let t6;
  let t7;
  if ($[11] !== setSidebarOpen || $[12] !== sidebarPreference) {
    t6 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const mediaQueryMD = window.matchMedia("(min-width: 768px)");
      const mediaQueryXL = window.matchMedia("(min-width: 1280px)");
      const updateSidebarState = () => {
        const isMD = mediaQueryMD.matches;
        const isXL = mediaQueryXL.matches;
        const shouldOpen = isMD && isXL;
        setSidebarOpen(currentOpen => {
          if (currentOpen !== shouldOpen) {
            return shouldOpen;
          }
          return currentOpen;
        });
      };
      if (sidebarPreference === "auto") {
        updateSidebarState();
      }
      const handleChange = () => {
        if (sidebarPreference === "manual") {
          return;
        }
        updateSidebarState();
      };
      if (mediaQueryMD.addEventListener && mediaQueryXL.addEventListener) {
        mediaQueryMD.addEventListener("change", handleChange);
        mediaQueryXL.addEventListener("change", handleChange);
        return () => {
          mediaQueryMD.removeEventListener("change", handleChange);
          mediaQueryXL.removeEventListener("change", handleChange);
        };
      } else {
        mediaQueryMD.addListener(handleChange);
        mediaQueryXL.addListener(handleChange);
        return () => {
          mediaQueryMD.removeListener(handleChange);
          mediaQueryXL.removeListener(handleChange);
        };
      }
    };
    t7 = [sidebarPreference, setSidebarOpen];
    $[11] = setSidebarOpen;
    $[12] = sidebarPreference;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  const t8 = showContextualSidebar || showSettingsSidebar;
  let t9;
  if ($[15] !== isEmbed) {
    t9 = !isEmbed && <Suspense fallback={<RailSkeleton />}><AppSwitcherRail /></Suspense>;
    $[15] = isEmbed;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== showContextualSidebar) {
    t10 = showContextualSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} />}><Sidebar /></Suspense>;
    $[17] = showContextualSidebar;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== showSettingsSidebar) {
    t11 = showSettingsSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} standalone={true} />}><SettingsSidebar /></Suspense>;
    $[19] = showSettingsSidebar;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = router.pathname;
  const t13 = fixedMain ? "min-h-0 overflow-hidden" : "overflow-y-auto [scrollbar-gutter:stable]";
  let t14;
  if ($[21] !== t13) {
    t14 = cn("flex-1 bg-transparent!", t13);
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex h-full items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== children) {
    t16 = <Suspense fallback={t15}>{children}</Suspense>;
    $[24] = children;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== router.pathname || $[27] !== t14 || $[28] !== t16) {
    t17 = <SidebarInset key={t12} className={t14}>{t16}</SidebarInset>;
    $[26] = router.pathname;
    $[27] = t14;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t17 || $[33] !== t9) {
    t18 = <div className="flex-1 flex overflow-hidden">{t9}{t10}{t11}{t17}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t17;
    $[33] = t9;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== handleSidebarChange || $[36] !== sidebarOpen || $[37] !== t18 || $[38] !== t8) {
    t19 = <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarChange} enableKeyboardShortcut={t8} className="flex-1 min-h-0 relative">{t18}</SidebarProvider>;
    $[35] = handleSidebarChange;
    $[36] = sidebarOpen;
    $[37] = t18;
    $[38] = t8;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== setShortcutsModalOpen || $[41] !== shortcutsModalOpen) {
    t20 = <ShortcutsModal open={shortcutsModalOpen} onOpenChange={setShortcutsModalOpen} />;
    $[40] = setShortcutsModalOpen;
    $[41] = shortcutsModalOpen;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <CommandPalette />;
    t22 = <LiveMetricsSheetLoader />;
    $[43] = t21;
    $[44] = t22;
  } else {
    t21 = $[43];
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t19 || $[46] !== t20) {
    t23 = <div className="flex flex-col h-dvh">{t19}{t20}{t21}{t22}</div>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  return t23;
}

// Every page shares these helpers so the OrgUrlSyncProvider > SidebarLayout
// element shape is identical across routes and React reconciles the persistent
// chrome instead of remounting it. useOrgUrlSync is a no-op on routes without
// a URL org and skips /admin/internal-monitors, so mounting it globally is safe.
export function getSidebarLayout(page: ReactElement) {
  return <OrgUrlSyncProvider>
      <SidebarLayout>{page}</SidebarLayout>
    </OrgUrlSyncProvider>;
}

// For pages whose content owns its scroll (scrollableMain={false}).
export function getFixedSidebarLayout(page: ReactElement) {
  return <OrgUrlSyncProvider>
      <SidebarLayout scrollableMain={false}>{page}</SidebarLayout>
    </OrgUrlSyncProvider>;
}